import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { partition } from "lodash";
import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";
import SwipeableViews from "react-swipeable-views";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import FixedTabContainer from "../fixedTab";
import DropDown from "../dropDown";
import Button from "../Button";
import Typography from "../Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";

const styles = theme => ({
  tabs_container: {
    flex: 1,
    marginRight: theme.margins.xs
  },
  tab_selected: {
    fontWeight: theme.fontWeight.bold
  },
  tab_root_blocked: {
    fontSize: 15,
    whiteSpace: "nowrap",
    textTransform: "none",
    height: theme.header_height + 2, // border bottom
    margin: 0,
    width: 150
  },
  tab_root: {
    fontSize: 15,
    whiteSpace: "nowrap",
    textTransform: "none",
    height: theme.header_height + 2, // border bottom
    margin: 0,
    width: 150,
    "&:hover": {
      background: theme.palette.hoverColor
    }
  },
  container: {
    paddingRight: 4,
    [theme.breakpoints.up("md")]: {
      paddingRight: theme.margins.xs
    }
  },
  feedBox: {
    position: "absolute",
    right: 124,
    top: 16,
    width: 500,
    height: 270,
    [theme.breakpoints.down("sm")]: {
      width: 375,
      right: 0
    },
    border: "1px solid #EEE",
    background: "#FFF",
    zIndex: 1,
    padding: 24,
    boxSizing: "border-box",
    boxShadow: "0px 4px 4px rgba(88, 88, 88, 0.25)"
  },
  feedContainer: {
    margin: "16px 0px 16px 0px"
  },
  textArea: {
    width: "100%",
    height: "136px !important",
    overflowY: "scroll",
    margin: 0,
    fontSize: 16,
    fontFamily: "Open Sans, sans-serif",
    color: "#000",
    padding: 12,
    "&:focus": {
      outline: "1px solid #DDD"
    },
    "&:active": {
      outline: "1px solid #DDD"
    },
    border: "1px solid #DDD",
    boxSizing: "border-box"
  },
  upArrow: {
    borderColor: "#EEE transparent",
    borderStyle: "solid",
    height: 0,
    width: 0,
    borderWidth: "0px 16px 16px 16px",
    position: "absolute",
    right: "24px",
    top: "-16px",
    [theme.breakpoints.down("sm")]: {
      right: "52px"
    }
  },
  upArrow2: {
    borderColor: "#FFF transparent",
    borderStyle: "solid",
    height: 0,
    width: 0,
    borderWidth: "0px 14px 14px 14px",
    position: "absolute",
    right: "26px",
    top: "-14px",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      right: "54px"
    }
  }
});

function concated_content(
  icons,
  secondary_cta_text,
  secondary_cta_action,
  secondary_cta_props
) {
  const content = Boolean(icons && icons.length) ? icons : [];
  return secondary_cta_text && secondary_cta_action
    ? content.concat([
      {
        name: secondary_cta_text,
        type: "icon-s-remove",
        onClick: secondary_cta_action,
        ...secondary_cta_props
      }
    ])
    : content;
}

const IconButtonComponent = React.memo(
  withStyles(theme => ({
    icon_margins: {}
  }))(({ classes, no_margin, type, ...props }) => {
    return (
      <Button
        variant="icon"
        className={classes.icon_margins}
        type={props.IconComponent ? undefined : type}
        {...props}
      >
        {props.name}
      </Button>
    );
  })
);

const XSButtonsComponent = React.memo(
  withStyles(theme => ({
    input_class: {
      padding: 0 //`0 ${theme.margins.xs}px 0 0`
    }
  }))(
    ({
      action_icons,
      secondary_cta_text,
      secondary_cta_action,
      secondary_cta_props,
      primary_cta_text,
      primary_cta_action,
      primary_cta_props,
      PrimaryButtonComponent,
      drop_down_props,
      dropdown_tooltip_text,
      classes
    }) => {
      const actionMaps = new Map();

      const { visible_icons, hidden_icons } = useMemo(() => {
        const [visible_icons, hidden_icons] = partition(
          action_icons,
          "xs_visible"
        );
        return { visible_icons, hidden_icons };
      }, [action_icons]);

      const hidden_content = concated_content(
        hidden_icons,
        secondary_cta_text,
        secondary_cta_action,
        secondary_cta_props
      );

      const handleChange = (e, hidden_content) => {
        const action =
          actionMaps.get(e.target.value) ||
          hidden_content.find(({ name }) => name === e.target.value).onClick;
        if (!Boolean(action)) {
          return;
        }

        actionMaps.set(action);
        return action(e, hidden_content);
      };

      //const show_single_icon = React.useMemo(() => !primary_cta_text && hidden_content && hidden_content.length === 1,[primary_cta_text,hidden_content.length]);
      const has_content = hidden_content && hidden_content.length;

      return (
        <>
          {visible_icons.map(({ name, xs_visible, type, ...icon_props }, i) => (
            <IconButtonComponent
              id={`xs_icons_button_${i}`}
              key={`xs_icons_button_${i}`}
              icon_path={type}
              {...icon_props}
            />
          ))}
          {Boolean(has_content && has_content === 1) ? (
            <IconButtonComponent
              id={`icon-button-right`}
              icon_path={hidden_content[0].type}
              {...hidden_content[0]}
            />
          ) : null}
          {Boolean(PrimaryButtonComponent) ? (
            <PrimaryButtonComponent {...primary_cta_props} />
          ) : (
              Boolean(primary_cta_text) && (
                <Button
                  size="xs"
                  onClick={primary_cta_action}
                  className={
                    Boolean(
                      has_content &&
                      has_content === 1 &&
                      primary_cta_props.state === "inactive"
                    )
                      ? classes.no_margin
                      : ""
                  }
                  {...primary_cta_props}
                >
                  {primary_cta_text}
                </Button>
              )
            )}
          {Boolean(has_content && has_content > 1) ? (
            <DropDown
              icon
              type="complex"
              values={hidden_content}
              displayEmpty
              dropdown_icon="icon-ic_more_vert_24px"
              handleChange={e => {
                handleChange(e, hidden_content);
              }}
              drop_down_menu_button
              dropdown_tooltip_text={dropdown_tooltip_text}
              input_class={classes.input_class}
              {...drop_down_props}
            />
          ) : null}
        </>
      );
    }
  )
);

XSButtonsComponent.propTypes = {
  action_icons: PropTypes.arrayOf(
    PropTypes.shape({
      xs_visible: PropTypes.bool,
      name: PropTypes.string,
      type: PropTypes.string,
      IconComponent: PropTypes.func
    })
  ),
  handleChange: PropTypes.func,
  menu_item_props: PropTypes.object
};

const ButtonsComponent = React.memo(
  withStyles(theme => ({
    primary: {
      margin: `0 0 0 4px`
    },
    margins: {
      margin: `0 ${theme.margins.xs / 2}px`
    },
    icon_margins: {}
  }))(
    ({
      action_icons,
      secondary_cta_text,
      secondary_cta_action,
      secondary_cta_props: secondaryCTAProps = {},
      PrimaryButtonComponent,
      primary_cta_text,
      primary_cta_action,
      primary_cta_props,
      classes
    }) => {
      const { IconComponent, ...secondary_cta_props } = secondaryCTAProps;

      return (
        <>
          {Boolean(action_icons && action_icons.length)
            ? action_icons.map(({ type, xs_visible, ...props }, i) => (
              <IconButtonComponent
                key={`icon-button-${i}`}
                id={`icon-button-${i}`}
                icon_path={type}
                {...props}
              />
            ))
            : null}
          {Boolean(secondary_cta_text && secondary_cta_action) && (
            <Button
              variant="tertiary"
              onClick={secondary_cta_action}
              {...secondary_cta_props}
            >
              {secondary_cta_text}
            </Button>
          )}
          {Boolean(PrimaryButtonComponent) ? (
            <PrimaryButtonComponent {...primary_cta_props} />
          ) : (
              Boolean(primary_cta_text && primary_cta_action) && (
                <Button
                  className={classes.primary}
                  onClick={primary_cta_action}
                  {...primary_cta_props}
                >
                  {primary_cta_text}
                </Button>
              )
            )}
        </>
      );
    }
  )
);

ButtonsComponent.propTypes = {
  secondary_cta_text: PropTypes.string,
  secondary_cta_action: PropTypes.func,
  PrimaryButtonComponent: PropTypes.func,
  primary_cta_text: PropTypes.string,
  primary_cta_action: PropTypes.func
};

const TabsComponent = React.memo(
  withStyles(styles)(
    ({
      value,
      handleChange,
      validate,
      block_tabs,
      tabs,
      create_buttons,
      type,
      tabs_class,
      feed,
      feedBack,
      handleChangeFeed,
      handleSendFeed,
      handleCancelFeed,
      classes: {
        tab_selected,
        tab_root,
        tab_root_blocked,
        tabs_container,
        container,
        feedBox,
        feedContainer,
        textArea,
        upArrow,
        upArrow2
      },
      ...props
    }) => {
      const md_up = useMediaQuery("(min-width:960px)");
      const xs_up = useMediaQuery("(min-width:600px)");
      const tabs_variant = xs_up ? "standard" : "fullWidth";
      const get_tab_label = React.useCallback(
        (long_text, short_text) => (md_up ? long_text : short_text),
        [md_up]
      );

      return (
        <Grid container alignItems="center" className={container}>
          <Tabs
            className={`${
              type === "tabs-buttons" ? tabs_container : undefined
              } ${tabs_class}`}
            value={value}
            variant={tabs_variant}
            onChange={(e, v) => handleChange(e, v, validate)}
            indicatorColor="secondary"
            textColor="secondary"
          >
            {tabs &&
              tabs.map(({ long_text, short_text }, index) => (
                <Tab
                  key={`tab_${index}`}
                  label={get_tab_label(
                    long_text,
                    short_text || `${index + 1}.`
                  )}
                  classes={{
                    selected: tab_selected,
                    root: block_tabs && index > 0 ? tab_root_blocked : tab_root
                  }}
                />
              ))}
          </Tabs>
          {type === "tabs-buttons" && md_up && <ButtonsComponent {...props} />}
          {type === "tabs-buttons" && !md_up && (
            <XSButtonsComponent {...props} />
          )}
          {feed && (
            <>
              <div className={feedBox}>
                <div className={upArrow}></div>
                <div className={upArrow2}></div>
                <Typography type="body1" color="black">
                  Give Feedback
                </Typography>
                <div className={feedContainer}>
                  <TextareaAutosize
                    placeholder="Please provide feedback here..."
                    className={textArea}
                    rowsMin={6}
                    value={feedBack}
                    onChange={handleChangeFeed}
                  />
                </div>
                <Button
                  onClick={handleSendFeed}
                  variant="primary"
                  size="sm"
                  state={feedBack ? "active" : "inactive"}
                  color="hylyBlue"
                >
                  Send
                </Button>
                <Button
                  onClick={handleCancelFeed}
                  variant="tertiary"
                  size="sm"
                  state="active"
                  color="hylyBlue"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </Grid>
      );
    }
  )
);

TabsComponent.propTypes = {
  value: PropTypes.number,
  handleChange: PropTypes.func,
  validate: PropTypes.func,
  block_tabs: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      long_text: PropTypes.string.isRequired,
      short_text: PropTypes.string
    })
  ),
  variant: PropTypes.oneOf(["standard", "fullWidth"]),
  classes: PropTypes.shape({
    tab_selected: PropTypes.string.isRequired,
    tab_root: PropTypes.string.isRequired,
    tabs_container: PropTypes.string.isRequired
  }),
  width: PropTypes.string
};

TabsComponent.defaultProps = {
  varinat: "standard"
};

class SecondNav extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      value: props.selected_tab || 0,
      s_scroll: false,
      s_blocked_tabs: false
    };
    this.setValue = this.setValue.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleChangeIndex = this.handleChangeIndex.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  handleScroll(e) {
    this.setState({ s_scroll: e.target.scrollTop !== 0 });
  }

  setValue(value) {
    this.setState({ value, s_blocked_tabs: false });
    window.scrollTo({ top: 0 });
  }

  async handleChange(e, value) {
    if (this.props.block_tabs) {
      return;
    }

    const { validate } = this.props;

    if (Boolean(validate)) {
      const errors = await validate();
      if (!Object.keys(errors).length) {
        this.setValue(value);
      } else {
        this.setState({ s_blocked_tabs: true });
      }
    } else {
      this.setValue(value);
    }
  }

  async handleChangeIndex(value, validate) {
    if (Boolean(validate)) {
      const errors = await validate();
      if (Object.keys(errors).length === 0) {
        this.setValue(value);
      }
    } else {
      this.setValue(value);
    }
  }

  render() {
    const {
      children: second_nav_children,
      block_tabs,
      validate,
      selected_tab,
      handleChange,
      swipeableViewsStyles,
      ...other_props
    } = this.props;

    return (
      <FixedTabContainer
        controlled_scroll={this.state.s_scroll}
        tab_component={
          <>
            <TabsComponent
              {...other_props}
              block_tabs={block_tabs || this.state.s_blocked_tabs}
              validate={validate}
              active={this.state.value}
              value={
                selected_tab !== undefined ? selected_tab : this.state.value
              }
              handleChange={
                Boolean(handleChange) ? handleChange : this.handleChange
              }
            />
          </>
        }
      >
        <SwipeableViews
          axis={"x"}
          index={selected_tab !== undefined ? selected_tab : this.state.value}
          onChangeIndex={e =>
            block_tabs ? null : this.handleChangeIndex(e, validate)
          }
          onScroll={this.handleScroll}
          style={swipeableViewsStyles}
        >
          {second_nav_children &&
            second_nav_children.map((Child, index) => {
              if (typeof Child === "function") {
                return <Child key={index} handleChange={this.handleChange} />;
              } else if (typeof Child === "object") {
                return React.cloneElement(Child, {
                  key: index,
                  handleChange: this.handleChange
                });
              } else return null;
            })}
        </SwipeableViews>
      </FixedTabContainer>
    );
  }
}

SecondNav.propTypes = {
  type: PropTypes.oneOf(["basic", "tabs-buttons"]),
  tab_component: React.isValidElement,
  children: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.object])
  ),
  validate: PropTypes.func,
  block_tabs: PropTypes.bool,
  selected_tab: PropTypes.number
};

SecondNav.defaultProps = {
  type: "basic"
};

export default SecondNav;
